import { Space } from "@babylonjs/core/Maths/math.axis";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Observable } from "@babylonjs/core/Misc/observable";
import { AppendTransformSolver } from "./appendTransformSolver";
import { IkSolver } from "./ikSolver";
import { MmdMorphController } from "./mmdMorphController";
import { MmdRuntimeBone } from "./mmdRuntimeBone";
/**
 * MmdModel is a class that controls the `MmdMesh` to animate the Mesh with MMD Runtime
 *
 * The mesh that instantiates `MmdModel` ignores some original implementations of Babylon.js and follows the MMD specifications
 *
 * The biggest difference is that the methods that get the absolute transform of `mesh.skeleton.bones` no longer work properly and can only get absolute transform through `bone.getFinalMatrix()`
 *
 * Final matrix is guaranteed to be updated after `MmdModel.afterPhysics()` stage
 */
export class MmdModel {
    /**
     * The mesh of this model
     */
    mesh;
    /**
     * The skeleton of this model
     *
     * This can be a instance of `Skeleton`, or if you are using a humanoid model, it will be referencing a virtualized bone tree
     *
     * So MmdModel.mesh.skeleton is not always equal to MmdModel.skeleton
     */
    skeleton;
    /**
     * The morph controller of this model
     *
     * The `MmdMorphController` not only wrapper of `MorphTargetManager` but also controls the CPU bound morphs (bone, material, group)
     */
    morph;
    _physicsModel;
    _logger;
    _sortedRuntimeBones;
    _sortedRuntimeRootBones;
    onCurrentAnimationChangedObservable;
    _animations;
    _animationIndexMap;
    _currentAnimation;
    constructor(mmdMesh, skeleton, materialProxyConstructor, mmdPhysics, logger) {
        this._logger = logger;
        const mmdMetadata = mmdMesh.metadata;
        const runtimeMesh = mmdMesh;
        runtimeMesh.metadata = {
            isRuntimeMmdModel: true,
            header: mmdMesh.metadata.header
        };
        this.mesh = runtimeMesh;
        this.skeleton = skeleton;
        // If you are not using MMD Runtime, you need to update the world matrix once. it could be waste of performance
        skeleton.prepare();
        this._disableSkeletonWorldMatrixUpdate(skeleton);
        const runtimeBones = this._buildRuntimeSkeleton(skeleton.bones, mmdMetadata.bones);
        const sortedBones = this._sortedRuntimeBones = [...runtimeBones];
        // sort must be stable (require ES2019)
        sortedBones.sort((a, b) => {
            return a.transformOrder - b.transformOrder;
        });
        const sortedRootBones = [];
        for (let i = 0; i < sortedBones.length; ++i) {
            const bone = sortedBones[i];
            if (bone.parentBone === null)
                sortedRootBones.push(bone);
        }
        this._sortedRuntimeRootBones = sortedRootBones;
        this.morph = new MmdMorphController(mmdMesh.morphTargetManager, runtimeBones, mmdMesh.material.subMaterials !== undefined
            ? mmdMesh.material
            : null, materialProxyConstructor, mmdMetadata.morphs, logger);
        if (mmdPhysics !== null) {
            for (let i = 0; i < sortedBones.length; ++i)
                sortedBones[i].updateLocalMatrix();
            for (let i = 0; i < sortedRootBones.length; ++i)
                sortedRootBones[i].updateWorldMatrix();
            this._physicsModel = mmdPhysics.buildPhysics(mmdMesh, runtimeBones, mmdMetadata.rigidBodies, mmdMetadata.joints, logger);
        }
        else {
            this._physicsModel = null;
        }
        this.onCurrentAnimationChangedObservable = new Observable();
        this._animations = [];
        this._animationIndexMap = new Map();
        this._currentAnimation = null;
    }
    /**
     * Dispose this model
     *
     * Restore the original bone matrix update behavior
     *
     * Dispose the physics resources if the physics is enabled
     */
    dispose() {
        this._enableSkeletonWorldMatrixUpdate();
        this._physicsModel?.dispose();
        this.onCurrentAnimationChangedObservable.clear();
        this.mesh.metadata = null;
    }
    /**
     * Get the sorted bones of this model
     *
     * The bones are sorted by `transformOrder`
     */
    get sortedRuntimeBones() {
        return this._sortedRuntimeBones;
    }
    /**
     * Add an animation to this model
     * @param animation MMD animation or MMD model animation group to add
     * @param retargetingMap Model bone name to animation bone name map
     */
    addAnimation(animation, retargetingMap) {
        let runtimeAnimation;
        if (animation.createRuntimeModelAnimation !== undefined) {
            runtimeAnimation = animation.createRuntimeModelAnimation(this, retargetingMap, this._logger);
        }
        else {
            throw new Error("animation is not MmdAnimation or MmdModelAnimationGroup or MmdCompositeAnimation. are you missing import \"babylon-mmd/esm/Runtime/Animation/mmdRuntimeModelAnimation\" or \"babylon-mmd/esm/Runtime/Animation/mmdRuntimeModelAnimationGroup\" or \"babylon-mmd/esm/Runtime/Animation/mmdCompositeRuntimeModelAnimation\"?");
        }
        this._animationIndexMap.set(animation.name, this._animations.length);
        this._animations.push(runtimeAnimation);
    }
    /**
     * Remove an animation from this model
     *
     * if index is out of range, do nothing
     * @param index The index of the animation to remove
     */
    removeAnimation(index) {
        const animation = this._animations[index];
        if (this._currentAnimation === animation) {
            this._currentAnimation = null;
            this._resetPose();
            this.onCurrentAnimationChangedObservable.notifyObservers(null);
        }
        this._animationIndexMap.delete(animation.animation.name);
        this._animations.splice(index, 1);
        animation.dispose?.();
    }
    /**
     * Set the current animation of this model
     * @param name The name of the animation to set
     * @throws {Error} if the animation is not found
     */
    setAnimation(name) {
        if (name === null) {
            if (this._currentAnimation !== null) {
                this._currentAnimation = null;
                this._resetPose();
                this.onCurrentAnimationChangedObservable.notifyObservers(null);
            }
            return;
        }
        const index = this._animationIndexMap.get(name);
        if (index === undefined) {
            throw new Error(`Animation '${name}' is not found.`);
        }
        if (this._currentAnimation !== null)
            this._resetPose();
        const animation = this._currentAnimation = this._animations[index];
        animation.induceMaterialRecompile(this._logger);
        this.onCurrentAnimationChangedObservable.notifyObservers(animation);
    }
    /**
     * Get the animations of this model
     */
    get runtimeAnimations() {
        return this._animations;
    }
    /**
     * Get the current animation of this model
     */
    get currentAnimation() {
        return this._currentAnimation;
    }
    /**
     * Reset the morph weights and IK enabled state of this model
     */
    resetState() {
        this.morph.resetMorphWeights();
        const sortedBones = this._sortedRuntimeBones;
        for (let i = 0; i < sortedBones.length; ++i) {
            const bone = sortedBones[i];
            if (bone.ikSolver !== null) {
                bone.ikSolver.enabled = true;
            }
        }
    }
    /**
     * Reset the rigid body positions and velocities of this model
     */
    initializePhysics() {
        this._physicsModel?.initialize();
    }
    /**
     * Before the physics stage, update animations and run MMD runtime solvers
     *
     * This method must be called before the physics stage
     *
     * if frameTime is null, animations are not updated
     * @param frameTime The time elapsed since the last frame in 30fps
     */
    beforePhysics(frameTime) {
        if (frameTime !== null) {
            if (this._currentAnimation !== null) {
                this._currentAnimation.animate(frameTime);
            }
        }
        this.morph.update();
        this._update(false);
        this._physicsModel?.syncBodies();
    }
    /**
     * After the physics stage, run MMD runtime solvers
     *
     * This method must be called after the physics stage
     */
    afterPhysics() {
        const physicsModel = this._physicsModel;
        if (physicsModel !== null) {
            physicsModel.syncBones();
        }
        this._update(true);
        this.mesh.skeleton._markAsDirty();
    }
    _update(afterPhysicsStage) {
        const sortedBones = this._sortedRuntimeBones;
        for (let i = 0; i < sortedBones.length; ++i) {
            const bone = sortedBones[i];
            if (bone.transformAfterPhysics !== afterPhysicsStage)
                continue;
            bone.updateLocalMatrix();
        }
        const sortedRootBones = this._sortedRuntimeRootBones;
        for (let i = 0; i < sortedRootBones.length; ++i) {
            const bone = sortedRootBones[i];
            if (bone.transformAfterPhysics !== afterPhysicsStage)
                continue;
            bone.updateWorldMatrix();
        }
        for (let i = 0; i < sortedBones.length; ++i) {
            const bone = sortedBones[i];
            if (bone.transformAfterPhysics !== afterPhysicsStage)
                continue;
            if (bone.appendTransformSolver !== null) {
                bone.appendTransformSolver.update();
                bone.updateLocalMatrix();
                bone.updateWorldMatrix();
            }
            if (bone.ikSolver !== null) {
                bone.ikSolver.solve();
                if (bone.ikSolver.enabled) {
                    bone.updateWorldMatrix();
                }
            }
        }
        for (let i = 0; i < sortedRootBones.length; ++i) {
            const bone = sortedRootBones[i];
            if (bone.transformAfterPhysics !== afterPhysicsStage)
                continue;
            bone.updateWorldMatrix();
        }
    }
    _buildRuntimeSkeleton(bones, bonesMetadata) {
        const runtimeBones = [];
        for (let i = 0; i < bonesMetadata.length; ++i) {
            const boneMetadata = bonesMetadata[i];
            runtimeBones.push(new MmdRuntimeBone(bones[i], boneMetadata));
        }
        for (let i = 0; i < bonesMetadata.length; ++i) {
            const boneMetadata = bonesMetadata[i];
            const bone = runtimeBones[i];
            const parentBoneIndex = boneMetadata.parentBoneIndex;
            if (0 <= parentBoneIndex && parentBoneIndex < runtimeBones.length) {
                const parentBone = runtimeBones[parentBoneIndex];
                bone.parentBone = parentBone;
                parentBone.childBones.push(bone);
            }
            if (boneMetadata.appendTransform !== undefined) {
                const targetBoneIndex = boneMetadata.appendTransform.parentIndex;
                if (0 <= targetBoneIndex && targetBoneIndex < runtimeBones.length) {
                    bone.appendTransformSolver = new AppendTransformSolver(boneMetadata.flag, boneMetadata.appendTransform, runtimeBones[targetBoneIndex]);
                }
                else {
                    this._logger.error(`Invalid append transform target bone index: ${targetBoneIndex}`);
                }
            }
            if (boneMetadata.ik !== undefined) {
                const ikMetadata = boneMetadata.ik;
                const targetBoneIndex = ikMetadata.target;
                if (0 <= targetBoneIndex && targetBoneIndex < runtimeBones.length) {
                    const ikSolver = bone.ikSolver = new IkSolver(bone, runtimeBones[targetBoneIndex]);
                    ikSolver.iteration = ikMetadata.iteration;
                    ikSolver.limitAngle = ikMetadata.rotationConstraint;
                    for (let j = 0; j < ikMetadata.links.length; ++j) {
                        const link = ikMetadata.links[j];
                        const linkBoneIndex = link.target;
                        if (0 <= linkBoneIndex && linkBoneIndex < runtimeBones.length) {
                            const linkBone = runtimeBones[linkBoneIndex];
                            ikSolver.addIkChain(linkBone, link.limitation?.minimumAngle ? Vector3.FromArray(link.limitation.minimumAngle) : null, link.limitation?.maximumAngle ? Vector3.FromArray(link.limitation.maximumAngle) : null);
                        }
                        else {
                            this._logger.error(`Invalid IK link bone index: ${linkBoneIndex}`);
                        }
                    }
                }
                else {
                    this._logger.error(`Invalid IK target bone index: ${targetBoneIndex}`);
                }
            }
        }
        return runtimeBones;
    }
    _originalComputeTransformMatrices = null;
    _disableSkeletonWorldMatrixUpdate(skeleton) {
        if (this._originalComputeTransformMatrices !== null)
            return;
        this._originalComputeTransformMatrices = skeleton._computeTransformMatrices;
        skeleton._computeTransformMatrices = function (targetMatrix, _initialSkinMatrix) {
            this.onBeforeComputeObservable.notifyObservers(this);
            for (let index = 0; index < this.bones.length; index++) {
                const bone = this.bones[index];
                bone._childUpdateId += 1;
                if (bone._index !== -1) {
                    const mappedIndex = bone._index === null ? index : bone._index;
                    bone.getAbsoluteInverseBindMatrix().multiplyToArray(bone.getFinalMatrix(), targetMatrix, mappedIndex * 16);
                }
            }
            this._identity.copyToArray(targetMatrix, this.bones.length * 16);
        };
    }
    _enableSkeletonWorldMatrixUpdate() {
        if (this._originalComputeTransformMatrices === null)
            return;
        this.skeleton._computeTransformMatrices = this._originalComputeTransformMatrices;
        this._originalComputeTransformMatrices = null;
    }
    _resetPose() {
        const sortedBones = this._sortedRuntimeBones;
        const position = new Vector3();
        const identityRotation = Quaternion.Identity();
        for (let i = 0; i < sortedBones.length; ++i) {
            const bone = sortedBones[i].linkedBone;
            bone.getRestMatrix().getTranslationToRef(position);
            bone.position = position;
            bone.setRotationQuaternion(identityRotation, Space.LOCAL);
        }
        this.mesh.skeleton._markAsDirty();
    }
}
